import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getSocketManager } from "@/shared/lib/socket-manager";
import { SOCKET_EVENTS } from "@/shared/api/types.socket";
import type {
  FollowUpQuestionsGeneratedEventParsed,
  FollowUpAnsweredStatusEventParsed,
} from "@/shared/api/schemas.socket";
import { QUERY_KEYS } from "@/shared/lib/constants";
import type { AuthSessionData, FollowUpStatus } from "@/shared/api/types";
import { logger } from "@/shared/lib/logger";

/**
 * Initializes the vendor Socket.io connection and registers global event handlers
 * that update TanStack Query caches. Call once in root layout after auth.
 */
export function useVendorSocket(isAuthenticated: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    const manager = getSocketManager();

    try {
      manager.connect();
    } catch (err) {
      logger.error("Failed to connect vendor socket", {
        error: err instanceof Error ? err.message : String(err),
      });
      return;
    }

    // Reconnect recovery — invalidate auth session and all chat message queries
    const socket = manager.getSocket();
    let isInitialConnect = true;
    const handleReconnect = () => {
      if (isInitialConnect) {
        isInitialConnect = false;
        return;
      }
      logger.info("Vendor socket reconnected — invalidating queries");
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.authSession });
      void queryClient.invalidateQueries({ queryKey: ["chat-messages"] });
    };
    socket?.on("connect", handleReconnect);

    // Assessment rooms ready → re-fetch auth + rooms to pick up new status
    const handleAssessmentRoomsReady = () => {
      logger.info(
        "Assessment rooms ready — invalidating auth and rooms queries",
      );
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.authSession });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.assessmentRooms,
      });
    };

    // Follow-up questions generated → update chatId in auth cache + status
    const handleFollowUpGenerated = (
      data: FollowUpQuestionsGeneratedEventParsed,
    ) => {
      logger.info("Follow-up questions generated", { chatId: data.chat_id });
      queryClient.setQueryData<AuthSessionData>(
        QUERY_KEYS.authSession,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            chatId: data.chat_id,
            assessmentStatus: "followup-questions-input-needed" as const,
          };
        },
      );
      // Do NOT seed chat messages here — GET /v0/vendor/chat/messages/{chatId} owns that
      // cache. Seeding here would populate the cache before useChatMessages mounts, causing
      // staleTime:Infinity to skip the GET call and lose conversation history on refresh.
      queryClient.setQueryData<FollowUpStatus>(
        QUERY_KEYS.followUpStatus,
        () => ({
          answeredCount: 0,
          answeredPercent: 0,
          unansweredCount: data.questions.length,
          totalQuestions: data.questions.length,
          areAllQuestionsAnswered: false,
        }),
      );
    };

    // Follow-up answered status → update follow-up status cache
    const handleAnsweredStatus = (data: FollowUpAnsweredStatusEventParsed) => {
      queryClient.setQueryData<FollowUpStatus>(
        QUERY_KEYS.followUpStatus,
        () => ({
          answeredCount: data.answered_count,
          answeredPercent: data.answered_percent,
          unansweredCount: data.unanswered_count,
          totalQuestions: data.total_questions,
          areAllQuestionsAnswered: data.are_all_questions_answered,
        }),
      );

      // If all answered, update assessment status to completed
      if (data.are_all_questions_answered) {
        queryClient.setQueryData<AuthSessionData>(
          QUERY_KEYS.authSession,
          (old) => {
            if (!old) return old;
            return { ...old, assessmentStatus: "completed" as const };
          },
        );
      }
    };

    manager.on(
      SOCKET_EVENTS.ASSESSMENT_ROOMS_READY,
      handleAssessmentRoomsReady,
    );
    manager.on(SOCKET_EVENTS.FOLLOW_UP_GENERATED, handleFollowUpGenerated);
    manager.on(SOCKET_EVENTS.ANSWERED_STATUS, handleAnsweredStatus);

    return () => {
      socket?.off("connect", handleReconnect);
      manager.off(
        SOCKET_EVENTS.ASSESSMENT_ROOMS_READY,
        handleAssessmentRoomsReady,
      );
      manager.off(SOCKET_EVENTS.FOLLOW_UP_GENERATED, handleFollowUpGenerated);
      manager.off(SOCKET_EVENTS.ANSWERED_STATUS, handleAnsweredStatus);
      manager.disconnect();
    };
  }, [isAuthenticated, queryClient]);
}
